import React, { JSX, useRef, useState } from 'react'
import { Link } from 'react-router'
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  EnvelopeIcon,
  KeyIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { PRIVACY_POLICY_URL, accountDeletionContact } from 'lib/externalLinks'
import { Avatar } from 'components/Avatar'
import { ErrorInline } from 'components/ErrorInline'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { assetS3KeyFromUpload } from 'components/ImageUpload'
import { useAuth, StreamToolsUser } from 'context/user'
import { clearStoredSocketToken } from 'context/socketToken'
import { graphqlRequest, GENERIC_ERROR } from 'lib/helpers'
import { ImageUploadUrl } from 'types/api'
import updateMyDisplayNameMutation from 'graphql/updateMyDisplayName.graphql'
import requestMyPasswordResetMutation from 'graphql/requestMyPasswordReset.graphql'
import requestProfilePictureUploadUrlMutation from 'graphql/requestProfilePictureUploadUrl.graphql'
import setMyProfilePictureMutation from 'graphql/setMyProfilePicture.graphql'

const NAME_MAX = 64

const PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const PICTURE_MAX_BYTES = 2 * 1024 * 1024
const PICTURE_EDGE = 512

const NOT_CONFIGURED = /not configured/i

const ACCOUNT_UNAVAILABLE = 'Account management is not available yet.'

const NAME_SAVED_NOTE =
  'Saved.'

const RESET_SENT_NOTE =
  'Password reset email sent to the email on your account. Follow the ' +
  'link in it to choose a new password.'

const accountErrorCopy = (err: unknown): string => {
  if (!(err instanceof Error) || err.message === '') return GENERIC_ERROR
  if (NOT_CONFIGURED.test(err.message)) return ACCOUNT_UNAVAILABLE
  return err.message
}

interface DisplayNameResult { name: string }
interface PasswordResetResult { sent: boolean }
interface ProfilePictureResult { pictureUrl: string }

const cropSquare = async (file: File): Promise<Blob> => {
  const bitmap = await createImageBitmap(file)
  try {
    const side = Math.min(bitmap.width, bitmap.height)
    const sx = Math.floor((bitmap.width - side) / 2)
    const sy = Math.floor((bitmap.height - side) / 2)
    const canvas = document.createElement('canvas')
    canvas.width = PICTURE_EDGE
    canvas.height = PICTURE_EDGE
    const ctx = canvas.getContext('2d')
    if (ctx === null) throw new Error('Could not process the image.')
    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, PICTURE_EDGE, PICTURE_EDGE)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, file.type, 0.92)
    )
    if (blob === null) throw new Error('Could not process the image.')
    return blob
  } finally {
    bitmap.close()
  }
}

const AccountSection = ({
  title,
  children,
  testId,
  danger = false
}: {
  title: string
  children: React.ReactNode
  testId: string
  danger?: boolean
}): JSX.Element => (
  <section
    data-testid={testId}
    className={`rounded-xl border ${danger ? 'border-red-500/20' : 'border-white/5'} bg-dm-bg-secondary p-6`}
  >
    <h2 className='mb-2 text-sm font-semibold uppercase tracking-wider text-dm-text-accent'>
      {title}
    </h2>
    {children}
  </section>
)

const SuccessNote = ({
  children,
  testId
}: {
  children: React.ReactNode
  testId: string
}): JSX.Element => (
  <div
    data-testid={testId}
    className='mt-3 flex max-w-2xl items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300'
  >
    <CheckIcon className='size-4 shrink-0' aria-hidden='true' />
    <span className='flex-1'>{children}</span>
  </div>
)

const DeleteAccountCopy = (): JSX.Element => {
  const contact = accountDeletionContact()
  const link = (
    <a
      href={contact.href}
      {...(contact.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      data-testid='account-delete-contact-link'
      className='font-medium text-dm-text-primary underline hover:text-white'
    >
      {contact.label}
    </a>
  )
  return (
    <div className='max-w-2xl space-y-2 text-sm text-dm-text-secondary'>
      <p>
        To delete your account, {link} and ask us to delete it. Use the email address on your account so we can confirm the request is yours.
      </p>
      <p className='text-xs text-dm-text-accent'>
        Deletion removes your login, every stream you own and everything in
        it (including streams teammates use), your overlays, merch boards
        and uploaded images, and your Twitch and YouTube connections.
      </p>
      <p className='text-xs text-dm-text-accent'>
        It cannot be undone. What we keep is described in our{' '}
        <Link to={PRIVACY_POLICY_URL} className='text-dm-text-primary underline hover:text-white'>
          privacy policy
        </Link>.
      </p>
    </div>
  )
}

const AccountReady = ({
  user,
  token
}: {
  user: StreamToolsUser
  token: string
}): JSX.Element => {
  const { logout } = useAuth0()
  const { updateProfilePicture } = useAuth()

  const [nameInput, setNameInput] = useState(user.name ?? '')
  const [nameBusy, setNameBusy] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameSaved, setNameSaved] = useState(false)

  const [resetBusy, setResetBusy] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)

  const pictureInputRef = useRef<HTMLInputElement | null>(null)
  const [pictureUrl, setPictureUrl] = useState<string | null>(null)
  const [pictureBusy, setPictureBusy] = useState(false)
  const [pictureError, setPictureError] = useState<string | null>(null)
  const [pictureSaved, setPictureSaved] = useState(false)

  const trimmedName = nameInput.trim()
  const nameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX

  const handleSaveName = async (): Promise<void> => {
    if (!nameValid || nameBusy) return
    setNameBusy(true)
    setNameError(null)
    setNameSaved(false)
    try {
      const { displayName } = await graphqlRequest<{ displayName: DisplayNameResult }>(
        updateMyDisplayNameMutation, { name: trimmedName }, token
      )
      setNameInput(displayName.name)
      setNameSaved(true)
    } catch (err) {
      setNameError(accountErrorCopy(err))
    } finally {
      setNameBusy(false)
    }
  }

  const handleSendReset = async (): Promise<void> => {
    if (resetBusy) return
    setResetBusy(true)
    setResetError(null)
    setResetSent(false)
    try {
      const { reset } = await graphqlRequest<{ reset: PasswordResetResult }>(
        requestMyPasswordResetMutation, {}, token
      )
      if (reset.sent) {
        setResetSent(true)
      } else {
        setResetError(GENERIC_ERROR)
      }
    } catch (err) {
      setResetError(accountErrorCopy(err))
    } finally {
      setResetBusy(false)
    }
  }

  const handlePictureFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file == null || pictureBusy) return

    if (!PICTURE_TYPES.includes(file.type)) {
      setPictureError('Profile pictures must be PNG, JPEG, or WebP.')
      return
    }

    setPictureBusy(true)
    setPictureError(null)
    setPictureSaved(false)
    try {
      const blob = await cropSquare(file)
      if (!PICTURE_TYPES.includes(blob.type)) {
        throw new Error('Could not encode the image in a supported format.')
      }
      if (blob.size > PICTURE_MAX_BYTES) {
        throw new Error('The processed image is too large (max 2 MB). Try a smaller source image.')
      }

      const { upload } = await graphqlRequest<{ upload: ImageUploadUrl }>(
        requestProfilePictureUploadUrlMutation,
        { contentType: blob.type, contentLength: blob.size },
        token
      )
      const res = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type },
        body: blob
      })
      if (!res.ok) {
        throw new Error(`Upload failed (${res.status}). Please try again.`)
      }
      const { picture } = await graphqlRequest<{ picture: ProfilePictureResult }>(
        setMyProfilePictureMutation,
        { s3Key: assetS3KeyFromUpload(upload) },
        token
      )
      setPictureUrl(picture.pictureUrl)
      updateProfilePicture(picture.pictureUrl)
      setPictureSaved(true)
    } catch (err) {
      setPictureError(accountErrorCopy(err))
    } finally {
      setPictureBusy(false)
    }
  }

  const handleLogout = (): void => {
    clearStoredSocketToken()
    void logout({ logoutParams: { returnTo: globalThis.location.origin } })
  }

  return (
    <div className='flex flex-1' data-testid='account-page'>
      <main className='flex flex-1 flex-col px-4 py-4'>
        <header className='mb-6 flex items-center gap-3'>
          <UserCircleIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
          <h1 className='text-2xl font-semibold text-dm-text-primary'>Account</h1>
        </header>

        <div className='flex flex-col gap-6'>

          <AccountSection title='Display name' testId='account-display-name'>
            <p className='text-xs text-dm-text-accent'>
              The name shown to you and your teammates across FlightDeck.
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); void handleSaveName() }}
              className='mt-4 flex flex-col gap-3 sm:flex-row sm:items-start'
            >
              <div className='flex-1'>
                <input
                  type='text'
                  value={nameInput}
                  onChange={(e) => {
                    setNameInput(e.target.value)
                    setNameSaved(false)
                  }}
                  placeholder='Your display name'
                  aria-label='Display name'
                  disabled={nameBusy}
                  data-testid='account-display-name-input'
                  className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
                />
                {trimmedName.length > NAME_MAX && (
                  <p className='mt-1 text-[11px] text-red-300' data-testid='account-display-name-too-long'>
                    Display names can be up to {NAME_MAX} characters.
                  </p>
                )}
              </div>
              <button
                type='submit'
                disabled={nameBusy || !nameValid}
                data-testid='account-display-name-save'
                className='inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {nameBusy && <LoadingSpinner />}
                {nameBusy ? 'Saving…' : 'Save'}
              </button>
            </form>
            {nameSaved && (
              <SuccessNote testId='account-display-name-saved'>
                {NAME_SAVED_NOTE}
              </SuccessNote>
            )}
            {nameError !== null && (
              <div className='mt-3 max-w-2xl' data-testid='account-display-name-error'>
                <ErrorInline onDismiss={() => setNameError(null)}>
                  {nameError}
                </ErrorInline>
              </div>
            )}
          </AccountSection>

          <AccountSection title='Profile picture' testId='account-profile-picture'>
            <p className='text-xs text-dm-text-accent'>
              Shown in the corner of the app. Uploads are cropped square
              PNG, JPEG, or WebP, up to 2 MB.
            </p>
            <div className='mt-4 flex items-center gap-4'>
              <Avatar
                name={user.name}
                email={user.email}
                picture={pictureUrl ?? user.picture}
                className='h-16 w-16 text-lg'
              />
              <input
                ref={pictureInputRef}
                type='file'
                accept={PICTURE_TYPES.join(',')}
                onChange={(e) => { void handlePictureFile(e) }}
                className='hidden'
              />
              <button
                type='button'
                onClick={() => pictureInputRef.current?.click()}
                disabled={pictureBusy}
                data-testid='account-profile-picture-upload'
                className='inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                {pictureBusy ? <LoadingSpinner /> : <ArrowUpTrayIcon className='size-4' aria-hidden='true' />}
                {pictureBusy ? 'Uploading…' : 'Upload picture'}
              </button>
            </div>
            {pictureSaved && (
              <SuccessNote testId='account-profile-picture-saved'>
                Saved.
              </SuccessNote>
            )}
            {pictureError !== null && (
              <div className='mt-3 max-w-2xl' data-testid='account-profile-picture-error'>
                <ErrorInline onDismiss={() => setPictureError(null)}>
                  {pictureError}
                </ErrorInline>
              </div>
            )}
          </AccountSection>

          <AccountSection title='Password' testId='account-password'>
            <p className='text-xs text-dm-text-accent'>
              We’ll email a secure link to the email on your account. Follow
              it to choose a new password.
            </p>
            <button
              type='button'
              onClick={() => { void handleSendReset() }}
              disabled={resetBusy}
              data-testid='account-password-reset-button'
              className='mt-4 inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {resetBusy ? <LoadingSpinner /> : <KeyIcon className='size-4' aria-hidden='true' />}
              {resetBusy ? 'Sending…' : 'Send password reset email'}
            </button>
            {resetSent && (
              <SuccessNote testId='account-password-reset-sent'>
                {RESET_SENT_NOTE}
              </SuccessNote>
            )}
            {resetError !== null && (
              <div className='mt-3 max-w-2xl' data-testid='account-password-reset-error'>
                <ErrorInline onDismiss={() => setResetError(null)}>
                  {resetError}
                </ErrorInline>
              </div>
            )}
          </AccountSection>

          <AccountSection title='Email' testId='account-email'>
            <p className='flex items-center gap-2 text-sm text-dm-text-primary'>
              <EnvelopeIcon className='size-4 shrink-0 text-dm-text-accent' aria-hidden='true' />
              <span data-testid='account-email-value'>
                {user.email !== undefined && user.email !== '' ? user.email : '—'}
              </span>
            </p>
            <p className='mt-2 text-xs text-dm-text-accent'>
              Your email can’t be changed without contacting StagePilot support,
              it identifies your account across StagePilot products.
            </p>
          </AccountSection>

          <AccountSection title='Session' testId='account-session'>
            <p className='text-sm text-dm-text-secondary'>
              Signed in as <span className='font-medium text-dm-text-primary'>{user.name}</span>
              {user.email !== undefined && user.email !== '' && (
                <> ({user.email})</>
              )}.
            </p>
            <button
              type='button'
              onClick={handleLogout}
              data-testid='account-sign-out-button'
              className='mt-4 inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300'
            >
              <ArrowLeftStartOnRectangleIcon className='size-4' aria-hidden='true' />
              Sign out
            </button>
          </AccountSection>

          <AccountSection title='Delete account' testId='account-delete' danger>
            <DeleteAccountCopy />
          </AccountSection>
        </div>
      </main>
    </div>
  )
}

export const AccountPage = (): JSX.Element => {
  const { auth0Token, streamToolsUser } = useAuth()

  if (streamToolsUser === null) return <div>Unauthorized</div>
  if (auth0Token === undefined) return <LoadingPage />

  return <AccountReady user={streamToolsUser} token={auth0Token} />
}
